import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, Copy, Check, ChevronRight, Lock, Zap, Star, Users, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FEATURES = [
  { icon: '💎', text: 'AI Gems Detector — Stocks & Crypto signals' },
  { icon: '🎓', text: 'Full Investment Academy — 5 complete modules' },
  { icon: '📊', text: 'AI Chart Analyzer — Technical analysis in seconds' },
  { icon: '🤖', text: 'AI Investment Chat 24/7 — GPT-5, Claude, Gemini' },
  { icon: '🏦', text: 'Institutional Analysis — Goldman Sachs, BlackRock, McKinsey' },
  { icon: '📈', text: 'Real-time Market Sentiment — AI-powered' },
  { icon: '🎮', text: 'Trading Simulator — $10,000 virtual portfolio' },
  { icon: '⚡', text: 'Unlimited Portfolio Tracker' },
];

export default function VIPPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [claimed, setClaimed] = useState(false);
  const [vipCode, setVipCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [available, setAvailable] = useState(null);
  const [error, setError] = useState('');
  const [alreadyClaimed, setAlreadyClaimed] = useState(false);
  const codeRef = useRef(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/vip/stats`);
      setAvailable(res.data.available);
    } catch {}
  };

  const handleClaim = async () => {
    setLoading(true);
    setError('');
    try {
      const params = email ? `?email=${encodeURIComponent(email)}` : '';
      const res = await axios.post(`${API}/vip/claim${params}`);

      if (res.data.success) {
        setVipCode(res.data.code);
        setClaimed(true);
        setAvailable(prev => prev !== null ? Math.max(0, prev - 1) : null);
      } else if (res.data.code) {
        // Already claimed
        setVipCode(res.data.code);
        setAlreadyClaimed(true);
        setClaimed(true);
      } else {
        setError(res.data.message || 'No codes available');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(vipCode);
    setCopied(true);
    toast.success('Code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] overflow-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl" />
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-orange-500/30 to-transparent" />
      </div>

      <div className="relative max-w-3xl mx-auto px-6 py-16">

        {/* Top badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-orange-500/30 bg-orange-500/10 text-orange-400 text-sm font-semibold">
            <Shield className="w-4 h-4" />
            Do Not Bull — Early Access Program
          </div>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl sm:text-6xl font-black text-white mb-4 leading-tight">
            Unlock Your<br />
            <span className="bg-gradient-to-r from-orange-400 via-yellow-400 to-orange-500 bg-clip-text text-transparent">
              VIP Access
            </span>
          </h1>
          <p className="text-xl text-gray-400 max-w-xl mx-auto">
            Limited early access to DoNotBull — the AI-powered investing platform for smart investors.
          </p>

          {/* Available counter */}
          {available !== null && (
            <div className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full bg-zinc-900 border border-zinc-700">
              <div className={`w-2 h-2 rounded-full animate-pulse ${available > 10 ? 'bg-green-400' : available > 3 ? 'bg-yellow-400' : 'bg-red-400'}`} />
              <span className="text-sm font-semibold text-white">
                {available > 0
                  ? <><span className={available <= 10 ? 'text-yellow-400 font-black' : 'text-green-400 font-black'}>{available}</span> VIP access{available > 1 ? 'es' : ''} remaining</>
                  : <span className="text-red-400">All VIP codes claimed</span>
                }
              </span>
              {available <= 10 && available > 0 && (
                <span className="text-xs text-orange-400 font-semibold animate-pulse ml-1">— Almost gone!</span>
              )}
            </div>
          )}
        </div>

        {!claimed ? (
          <>
            {/* Claim form */}
            <div className="relative bg-gradient-to-br from-zinc-900 to-black rounded-3xl border border-zinc-800 p-8 sm:p-10 mb-8">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent rounded-t-3xl" />

              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center">
                  <Star className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h2 className="text-xl font-black text-white">Activate Your VIP Access</h2>
                  <p className="text-gray-400 text-sm">Instant full access — no credit card required</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    Your Email <span className="text-gray-600">(optional — to keep your code safe)</span>
                  </label>
                  <Input
                    data-testid="vip-email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="bg-zinc-900 border-zinc-700 text-white h-12 text-base"
                  />
                </div>

                {error && (
                  <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <Button
                  data-testid="claim-vip-btn"
                  onClick={handleClaim}
                  disabled={loading || available === 0}
                  className="w-full h-14 text-lg font-black rounded-2xl bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-black disabled:opacity-50"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      Activating...
                    </div>
                  ) : available === 0 ? (
                    'No codes available'
                  ) : (
                    <div className="flex items-center gap-2">
                      <Zap className="w-5 h-5" />
                      Activate My VIP Access
                      <ArrowRight className="w-5 h-5" />
                    </div>
                  )}
                </Button>

                <p className="text-center text-xs text-gray-600">
                  <Lock className="w-3 h-3 inline mr-1" />
                  One VIP code per person • Lifetime access • No subscription required
                </p>
              </div>
            </div>

            {/* Features preview */}
            <div>
              <p className="text-center text-sm text-gray-500 font-semibold uppercase tracking-widest mb-5">
                What's included in your VIP access:
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {FEATURES.map((f, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-zinc-900/60 rounded-xl border border-zinc-800/50">
                    <span className="text-xl flex-shrink-0">{f.icon}</span>
                    <span className="text-gray-300 text-sm">{f.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          /* SUCCESS STATE */
          <div className="text-center">
            {/* Success animation */}
            <div className="relative mb-8">
              <div className="w-28 h-28 rounded-full bg-gradient-to-br from-orange-500/20 to-yellow-500/20 border-2 border-orange-500/50 flex items-center justify-center mx-auto">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center">
                  <Check className="w-10 h-10 text-black font-black" />
                </div>
              </div>
              <div className="absolute inset-0 rounded-full bg-orange-500/10 animate-ping" />
            </div>

            <h2 className="text-4xl font-black text-white mb-3">
              {alreadyClaimed ? 'Welcome back!' : 'Your VIP Access is Activated!'}
            </h2>
            <p className="text-gray-400 text-lg mb-10">
              {alreadyClaimed
                ? 'You already have a VIP code. Here it is:'
                : 'Congratulations — you now have lifetime full access to DoNotBull.'}
            </p>

            {/* VIP Code display */}
            <div className="relative bg-gradient-to-br from-zinc-900 to-black rounded-3xl border border-orange-500/30 p-8 mb-8 max-w-md mx-auto">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-orange-500/60 to-transparent rounded-t-3xl" />

              <p className="text-xs text-gray-500 font-semibold uppercase tracking-widest mb-3">Your VIP Code</p>

              <div
                data-testid="vip-code-display"
                ref={codeRef}
                className="text-4xl font-black text-orange-400 font-mono tracking-[0.3em] py-4 px-6 bg-orange-500/10 rounded-2xl border border-orange-500/30 mb-4 cursor-pointer select-all"
                onClick={copyCode}
              >
                {vipCode}
              </div>

              <button
                data-testid="copy-code-btn"
                onClick={copyCode}
                className={`flex items-center gap-2 mx-auto text-sm font-semibold transition-all px-4 py-2 rounded-full ${copied ? 'bg-green-500/20 text-green-400' : 'bg-zinc-800 text-gray-400 hover:text-white'}`}
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy code'}
              </button>
            </div>

            {/* Instructions */}
            <div className="bg-zinc-900/60 rounded-2xl border border-zinc-800 p-6 mb-8 max-w-md mx-auto text-left">
              <p className="text-sm font-bold text-white mb-3">How to use your code:</p>
              <div className="space-y-2 text-sm text-gray-400">
                <div className="flex items-start gap-2">
                  <span className="text-orange-400 font-bold w-5 flex-shrink-0">1.</span>
                  Click "Access DoNotBull" below
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-400 font-bold w-5 flex-shrink-0">2.</span>
                  Click "Sign up" and create your account
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-400 font-bold w-5 flex-shrink-0">3.</span>
                  Enter your VIP code <span className="font-mono text-orange-400 font-bold">{vipCode}</span> in the optional field
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-orange-400 font-bold w-5 flex-shrink-0">4.</span>
                  Enjoy lifetime full access — completely free!
                </div>
              </div>
            </div>

            <Button
              data-testid="access-app-btn"
              onClick={() => navigate('/')}
              className="w-full max-w-md mx-auto h-14 text-lg font-black rounded-2xl bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600 text-black"
            >
              Access DoNotBull
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>

            <p className="text-xs text-gray-600 mt-4">
              Do Not Bull — a brand of Strategy Intelligence Solutions LTD (No. 16760736)
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
